import { forwardRef } from 'react';

import { useMergedRef } from '@/hook';
import { uuid } from '@/util';

import { VBaseSelect, useVBaseSelectContext } from '../VBaseSelect';
import type { VBaseTableColumnRenederer, VBaseTableData } from '../VBaseTable';
import { VFlex } from '../VFlex';
import { KeyboardArrowDown } from '../asset';
import { useVTableContext } from './hook';

const FilterColumnRenderer = <T extends VBaseTableData>({
  columnId,
  columnRenderer,
}: {
  columnId: keyof T;
  columnRenderer: VBaseTableColumnRenederer<T>;
}) => {
  const { columnFilter, onColumnFilterChange } = useVTableContext();

  const currentColumnFilter = columnFilter?.find(({ id }) => id === columnId);
  const originalRenderer = columnRenderer?.[columnId];
  const originalContent =
    typeof originalRenderer === 'function'
      ? originalRenderer(columnId)
      : String(columnId);
  const isAllSelected = currentColumnFilter?.itemList.every(
    item => item.isSelected,
  );
  const isAllUnselected = currentColumnFilter?.itemList.every(
    item => !item.isSelected,
  );
  const allUUID = uuid();
  const expandedColumnFilter = [
    {
      value: allUUID,
      label: 'All',
      isSelected: isAllSelected || isAllUnselected,
    },
    ...(currentColumnFilter?.itemList ?? []),
  ];
  const orgValue = currentColumnFilter?.itemList
    .filter(item => item.isSelected)
    .map(item => item.value);

  const value =
    isAllSelected || isAllUnselected
      ? [allUUID, ...(orgValue ?? [])]
      : orgValue;

  return (
    <VFlex items="center" gap={0.5} style={{ cursor: 'pointer' }}>
      {originalContent}
      <VBaseSelect
        itemList={expandedColumnFilter}
        multiple
        value={value}
        onItemClick={e => {
          const isAllClicked = e.target.item.value === allUUID;
          const targetColumnFilterItem = currentColumnFilter?.itemList.find(
            item => item.value === e.target.item.value,
          );
          const nextColumnFilter = isAllClicked
            ? currentColumnFilter?.itemList.map(item => ({
                ...item,
                isSelected: false,
              }))
            : currentColumnFilter?.itemList.map(item =>
                item.value === targetColumnFilterItem?.value
                  ? { ...item, isSelected: !item.isSelected }
                  : item,
              );
          const nextColumnFilterList = columnFilter?.map(filter =>
            filter.id === columnId
              ? { ...filter, itemList: nextColumnFilter ?? [] }
              : filter,
          );

          onColumnFilterChange?.(nextColumnFilterList ?? []);
        }}
      >
        <FilterPopup />
      </VBaseSelect>
    </VFlex>
  );
};

const FilterPopup = forwardRef((_, ref) => {
  const { $displayValue } = useVBaseSelectContext();
  const refs = useMergedRef($displayValue, ref);

  return <KeyboardArrowDown className="size-4" ref={refs} />;
});

export default FilterColumnRenderer;
